import { createFileRoute, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listPrintJobs, setPrintJobStatus } from "@/lib/admin-config.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMemo, useState } from "react";
import { Printer, RefreshCw, X, Eye, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

export const Route = createFileRoute("/_authenticated/app/admin/print-queue")({
  validateSearch: zodValidator(dateRangeSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: PrintQueuePage,
});

type Status = "queued" | "sent" | "printed" | "failed" | "cancelled";

function PrintQueuePage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Status | "all">("queued");
  const [preview, setPreview] = useState<string | null>(null);
  const fetchJobs = useServerFn(listPrintJobs);
  const setStatusFn = useServerFn(setPrintJobStatus);
  const urlSearch = Route.useSearch();
  type S = typeof urlSearch;
  const navigate = useNavigate({ from: Route.fullPath });
  const update = (patch: Partial<S>) => navigate({ search: (prev: S) => ({ ...prev, ...patch }) });
  const sort = urlSearch.sort ?? "desc";
  // Default last 24h
  const effectiveStart = urlSearch.start
    ?? (urlSearch.end ? undefined : new Date(Date.now() - 86400000).toISOString().slice(0, 10));
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: effectiveStart, end: urlSearch.end }),
    [effectiveStart, urlSearch.end],
  );

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ["print-jobs", tenantId, tab, fromIso, toIso, sort],
    enabled: !!tenantId,
    refetchInterval: 5000,
    queryFn: () => runServerFn(
      () => fetchJobs({ data: { tenantId: tenantId!, status: tab, fromIso, toIso, sort } }),
      "Loading jobs",
    ),
  });

  const setStatus = async (id: string, status: Status) => {
    try {
      await runServerFn(() => setStatusFn({ data: { id, status } }), "Updating job");
      qc.invalidateQueries({ queryKey: ["print-jobs"] });
    } catch { /* */ }
  };

  const downloadAsFile = (id: string, payload: string, format: string) => {
    const blob = new Blob([payload], { type: format === "zpl" ? "application/zpl" : "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `print-job-${id}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">Print Queue</h2>
          <p className="text-sm text-muted-foreground">Label and document jobs queued for printers.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ["print-jobs"] })}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as Status | "all")}>
        <TabsList>
          <TabsTrigger value="queued">Queued</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
          <TabsTrigger value="printed">Printed</TabsTrigger>
          <TabsTrigger value="failed">Failed</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex gap-2 flex-wrap mt-3">
        <DateRangeFilter
          value={{ start: urlSearch.start, end: urlSearch.end }}
          onChange={(next) => update({ start: next.start, end: next.end })}
        />
        <SortDirectionToggle
          value={sort}
          onChange={(s) => update({ sort: s === "desc" ? undefined : s })}
        />
      </div>

      <div className="border rounded-lg bg-card mt-4 divide-y">
        {isLoading ? (
          <div className="p-4 sm:p-6 text-sm text-muted-foreground">Loading…</div>
        ) : jobs.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No jobs in this queue.</div>
        ) : (
          jobs.map((j: any) => (
            <div key={j.id} className="px-4 py-3 flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center">
                {j.format === "zpl" ? <Printer className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  {j.kind.replace("_", " ")}
                  <Badge variant="outline" className="text-[10px] uppercase">{j.format}</Badge>
                  <Badge variant={j.status === "failed" ? "destructive" : j.status === "printed" ? "secondary" : "default"} className="text-[10px]">
                    {j.status}
                  </Badge>
                  {j.copies > 1 && <span className="text-xs text-muted-foreground">×{j.copies}</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {j.printer_name ?? "no printer assigned"} · {new Date(j.created_at).toLocaleString()}
                  {j.error ? <span className="text-destructive ml-2">· {j.error}</span> : null}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPreview(j.payload)}>
                <Eye className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => downloadAsFile(j.id, j.payload, j.format)}>
                Download
              </Button>
              {j.status === "queued" && (
                <Button variant="outline" size="sm" onClick={() => setStatus(j.id, "printed")}>
                  Mark printed
                </Button>
              )}
              {j.status === "failed" && (
                <Button variant="outline" size="sm" onClick={() => setStatus(j.id, "queued")}>
                  Retry
                </Button>
              )}
              {(j.status === "queued" || j.status === "sent") && (
                <Button variant="ghost" size="sm" onClick={() => setStatus(j.id, "cancelled")}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Print job payload</DialogTitle>
          </DialogHeader>
          <pre className="text-xs bg-muted p-3 rounded max-h-[60vh] overflow-auto whitespace-pre-wrap break-all">{preview}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
